import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React from 'react';
import { SubscriptionFeaturesProvider } from './subscription-features-provider';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Disable refetching on window focus and reconnect to prevent unnecessary refetches.
       * Source: https://tanstack.com/query/latest/docs/framework/react/reference/useQuery
       */
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      staleTime: Infinity
    }
  }
});

/**
 * A high-order component that wraps a component with an ExperimentationProvider
 *
 * @param {array[SubscriptionFeatureKey]} features - an array of items from SubscriptionFeatureKey enum
 * @param {React.Node} WrappedComponent - The Component you are wrapping with the SubscriptionFeaturesProvider
 *
 * @example
 * ```tsx
 * import {
 *  withSubscriptionFeaturesProvider,
 *  withSubscriptionFeatures,
 *  WithSubscriptionFeaturesProps
 * } from '@udemy/react-subscription-features-provider';
 *
 * interface MyComponentProps extends WithSubscriptionFeaturesProps { ... }
 *
 * class _MyComponent extends React.Component<MyComponentProps> {
 *  render() {
 *     return (
 *       <p>Is the feature enabled: {
 *          this.props.subscriptionFeaturesResult.subscriptionFeatures.get('some_features') ? 'YES' : 'NO'
 *       }
 *       </p>
 *       );
 *    }
 * }
 *
 * const features = [SubscriptionFeatureKey.SomeFeature]
 *
 * export const MyComponent = withSubscriptionFeaturesProvider( // this will fetch the GraphQL endpoint
 *    features,
 *    withSubscriptionFeatures(features, _MyComponent)          // This can be added to any child. It
 * )                                                             // will inject subscriptionFeaturesResult
 *                                                              // instance into the _MyComponent.
 * ```
 */

export function withSubscriptionFeaturesProvider(features, WrappedComponent) {
  // normalize features array
  features = [...new Set(features)];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const Component = /*#__PURE__*/React.forwardRef((props, ref) => /*#__PURE__*/React.createElement(QueryClientProvider, {
    client: queryClient
  }, /*#__PURE__*/React.createElement(SubscriptionFeaturesProvider, {
    features: features
  }, /*#__PURE__*/React.createElement(WrappedComponent, Object.assign({
    ref: ref
  }, props)))));
  Component.displayName = `WithSubscriptionFeaturesProvider(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return Component;
}
//# sourceMappingURL=with-subscription-features-provider.js.map